/**
 * Evaluates a single filter rule against a data item
 */

const evaluateRule = (item, rule) => {
  const fieldValue = item[rule.field];
  const { operator, value } = rule;

  switch (operator) {
    // Text operators
    case "equals":
      return String(fieldValue).toLowerCase() === String(value).toLowerCase();

    case "not_equals":
      return String(fieldValue).toLowerCase() !== String(value).toLowerCase();

    case "contains":
      return String(fieldValue)
        .toLowerCase()
        .includes(String(value).toLowerCase());

    case "not_contains":
      return !String(fieldValue)
        .toLowerCase()
        .includes(String(value).toLowerCase());

    case "starts_with":
      return String(fieldValue)
        .toLowerCase()
        .startsWith(String(value).toLowerCase());

    case "ends_with":
      return String(fieldValue)
        .toLowerCase()
        .endsWith(String(value).toLowerCase());

    // Number operators
    case "greater_than":
      return Number(fieldValue) > Number(value);

    case "less_than":
      return Number(fieldValue) < Number(value);

    case "greater_or_equal":
      return Number(fieldValue) >= Number(value);

    case "less_or_equal":
      return Number(fieldValue) <= Number(value);

    // Empty/null operators
    case "is_empty":
      return (
        fieldValue === null || fieldValue === undefined || fieldValue === ""
      );

    case "is_not_empty":
      return (
        fieldValue !== null && fieldValue !== undefined && fieldValue !== ""
      );

    // Date operators
    case "before":
      return new Date(fieldValue) < new Date(value);

    case "after":
      return new Date(fieldValue) > new Date(value);

    // Boolean operators
    case "is_true":
      return fieldValue === true;

    case "is_false":
      return fieldValue === false;

    default:
      return true;
  }
};

/**
 * Filters an array of data based on filter rules.
 * All rules are applied with AND logic (item must match all filters).
 *
 * @param data - Array of objects to filter
 * @param filters - Array of filter rules from FilterBuilder
 * @returns Filtered array of objects
 *
 * @example
 * const data = [
 *   { name: 'John', age: 30, email: 'john@example.com' },
 *   { name: 'Jane', age: 25, email: 'jane@example.com' }
 * ];
 *
 * const filters = [
 *   { id: '1', field: 'age', operator: 'greater_than', value: 26 }
 * ];
 *
 * const filtered = applyFilters(data, filters);
 * // Returns: [{ name: 'John', age: 30, email: 'john@example.com' }]
 */
export const applyFilters = (data, filters) => {
  if (!filters || filters.length === 0) {
    return data;
  }

  return data.filter((item) => {
    // Item must match ALL filter rules (AND logic)
    return filters.every((rule) => evaluateRule(item, rule));
  });
};
